import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnalyticsEventKeys, QuoteStatuses } from '../constants';
import currentDate from '../currentDate';
import {
  CurrencyPayResponse,
  CurrencyPayFinancingResponse
} from '../redux/models/currencyPay';
import { Dispatch, iRootState } from '../redux/store';
import getPaymentMethod from './getPaymentMethod';
import getPaymentStatus from './getPaymentStatus';
import getTransactionId from './getTransactionId';
import PaymentTypes from './PaymentTypes';
import useUpdatePaymentStatus from './useUpdatePaymentStatus';

const ConfigureCurrencyPayWidget = function ConfigureCurrencyPayWidget(): null {
  const dispatch = useDispatch<Dispatch>();
  const dealerId = useSelector((state: iRootState) => state.quote.dealerId);
  const quoteId = useSelector((state: iRootState) => state.quote.id);
  const scriptIsLoaded = useSelector(
    (state: iRootState) => state.currencyPay.scriptIsLoaded
  );
  const { updatePaymentStatus } = useUpdatePaymentStatus();

  useEffect(() => {
    if (!scriptIsLoaded || !quoteId || !dealerId) {
      return;
    }

    const updateQuoteStatus = function updateQuoteStatusInRedux(
      status: string
    ): void {
      dispatch.quote.setQuoteStatus(status, currentDate());
    };

    const postPaymentSuccessToAnalytics = function postPaymentSuccessToAnalytics(
      paymentStatus: string
    ): void {
      dispatch.analytics.sendEvent({
        eventType: AnalyticsEventKeys.PayNowAttempted,
        eventProperties: {
          paymentStatus,
          success: true
        }
      });
    };
    const postPaymentFailureToAnalytics = function postPaymentFailureToAnalytics(): void {
      dispatch.analytics.sendEvent({
        eventType: AnalyticsEventKeys.PayNowAttempted,
        eventProperties: {
          paymentStatus: 'Failed',
          success: false
        }
      });
    };

    const postFinancingApplicationSuccessToAnalytics = function postFinancingApplicationSuccessToAnalytics(
      applicationStatus: string
    ): void {
      dispatch.analytics.sendEvent({
        eventType: AnalyticsEventKeys.FinancingApplicationAttempted,
        eventProperties: {
          applicationStatus,
          success: true
        }
      });
    };

    const onSuccess = async function saveCurrencyPaySuccessfulResponse(
      responseData: CurrencyPayResponse[]
    ): Promise<void> {
      const paymentStatus = getPaymentStatus(responseData);
      const transactionId = getTransactionId(responseData);
      const paymentMethod = getPaymentMethod(responseData);
      postPaymentSuccessToAnalytics(paymentStatus);
      await updatePaymentStatus(paymentStatus, transactionId, paymentMethod);
      updateQuoteStatus(QuoteStatuses.Accepted);
    };

    const onUnderReview = async function saveCurrencyPayUnderReviewResponse(
      responseData: CurrencyPayFinancingResponse
    ): Promise<void> {
      const { applicationStatus, applicationId } = responseData;
      const paymentMethod = PaymentTypes.Financing;
      postFinancingApplicationSuccessToAnalytics(applicationStatus);
      await updatePaymentStatus(
        applicationStatus,
        applicationId.toString(),
        paymentMethod
      );
      updateQuoteStatus(QuoteStatuses.Accepted);
    };

    const onFailure = async function saveCurrencyPayFailedResponse(): Promise<void> {
      postPaymentFailureToAnalytics();
      await updatePaymentStatus(
        'Payment Unsuccessful',
        null,
        PaymentTypes.Unknown
      );
      updateQuoteStatus(QuoteStatuses.Accepted);
    };

    const onInit = function currencyPayWidgetInitialized(): void {
      dispatch.currencyPay.isInitialized();
    };

    dispatch.currencyPay.reinitialize({
      onSuccess,
      onFailure,
      onUnderReview,
      onInit
    });
  }, [
    dealerId,
    dispatch.analytics,
    dispatch.currencyPay,
    dispatch.quote,
    quoteId,
    scriptIsLoaded,
    updatePaymentStatus
  ]);

  return null;
};

export default ConfigureCurrencyPayWidget;
